import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, Paper, Card, CardContent, CardMedia, Button, Rating, CircularProgress, Chip, IconButton, FormControlLabel, Checkbox, Switch, AppBar, Tabs, Tab, Container } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { fetchVideosByDate, fetchLatestVideoDate, findNextDateWithVideos, findPrevDateWithVideos } from '../../api/archiveApi';
import { de } from 'date-fns/locale';
import { format, addDays, subDays } from 'date-fns';
import { de as dateFnsDe } from 'date-fns/locale';
import Dialog from '@mui/material/Dialog';
import FilterListIcon from '@mui/icons-material/FilterList';
import BlurhashImage from '../../components/BlurhashImage';
import VideoPlayer from '../../components/VideoPlayer';
import { useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VideocamIcon from '@mui/icons-material/Videocam';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import Link from '@mui/material/Link';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface VideoItem {
  id: string;
  fileName: string;
  timeStamp: string;
  cameraName: string;
  waschstrasse: string;
  richtigAusgeloest: boolean | null;
  hasRating?: boolean;
  thumbnailUrl: string;
  videoUrl: string;
  account?: string;
  cameraRef: string;
  blurhash?: string;
}

const Archive: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [allVideos, setAllVideos] = useState<VideoItem[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<VideoItem[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [navigating, setNavigating] = useState<boolean>(false);
  const [navigatingPrev, setNavigatingPrev] = useState<boolean>(false);
  const [navigatingNext, setNavigatingNext] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hideFailures, setHideFailures] = useState<boolean>(true);
  const [showOnlyRated, setShowOnlyRated] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(1); // 1 entspricht "Meldungen" im Tab-Menü
  const [feedback, setFeedback] = useState<string | null>(null);
  // Cache for loaded dates to avoid redundant API calls
  const [videosCache] = useState<Map<string, VideoItem[]>>(new Map());
  
  const navigate = useNavigate();

  // Helper to format date as cache key
  const formatDateKey = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  // Fetch latest date with videos on component mount
  useEffect(() => {
    const fetchLatestDate = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('📅 Archive: Initializing - Fetching latest date with videos...');
        
        // Fetch the latest date that has videos
        const latestDate = await fetchLatestVideoDate();
        
        if (latestDate) {
          console.log('📅 Archive: Found latest date with videos:', latestDate.toISOString());
          setSelectedDate(latestDate);
          // We'll load videos when selectedDate changes in the other useEffect
        } else {
          console.warn('⚠️ Archive: No date with videos found, using today as fallback');
          setSelectedDate(new Date());
          setError('Keine Videos in der Datenbank gefunden.');
          setLoading(false);
        }
      } catch (err) {
        console.error('❌ Archive: Error fetching latest date:', err);
        // On error, set today's date as fallback
        setSelectedDate(new Date());
        setError('Fehler beim Laden des neuesten Datums mit Videos.');
        setLoading(false);
      }
    };

    fetchLatestDate();
  }, []);

  // Load videos when date changes
  useEffect(() => {
    if (selectedDate) {
      loadVideos(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    applyFilters(allVideos);
  }, [hideFailures, showOnlyRated, allVideos]);

  const loadVideos = async (date: Date) => {
    setLoading(true);
    setError(null);
    setFeedback(null);
    
    try {
      const dateKey = formatDateKey(date);
      console.log('📅 Archive: Loading videos for date:', dateKey);
      
      // Check if we have this date in cache
      if (videosCache.has(dateKey)) {
        console.log('🗃️ Using cached videos for date:', dateKey);
        const cachedVideos = videosCache.get(dateKey) || [];
        setAllVideos(cachedVideos);
        applyFilters(cachedVideos);
        setLoading(false);
        setNavigatingPrev(false);
        setNavigatingNext(false);
        setNavigating(false);
        return;
      }
      
      // Otherwise fetch from API
      const videos = await fetchVideosByDate(date);
      console.log(`🎬 Archive: Loaded ${videos.length} videos successfully`);
      
      // Cache the result
      videosCache.set(dateKey, videos);
      
      if (videos.length > 0) {
        console.log('📽️ Archive: First video sample:', {
          id: videos[0].id,
          timestamp: videos[0].timeStamp,
          camera: videos[0].cameraName,
          blurhash: videos[0].blurhash
        });
        setAllVideos(videos);
        applyFilters(videos);
      } else {
        console.log('📅 Archive: No videos found for date:', date.toISOString());
        setAllVideos([]);
        setFilteredVideos([]);
        // Don't set an error here - it's normal to have days without videos
      }
    } catch (err: any) {
      console.error('❌ Archive: Error loading videos:', err);
      setError(err.message || 'Fehler beim Laden der Videos');
      setAllVideos([]);
      setFilteredVideos([]);
    } finally {
      setLoading(false);
      setNavigatingPrev(false);
      setNavigatingNext(false);
      setNavigating(false);
    }
  };

  const applyFilters = (videos: VideoItem[]) => {
    let filtered = [...videos];

    // Apply success/failure filter
    if (hideFailures) {
      // Show videos that are correctly triggered OR have an unknown status (null)
      filtered = filtered.filter(video => video.richtigAusgeloest === true || video.richtigAusgeloest === null);
    }

    // Apply rating filter
    if (showOnlyRated) {
      filtered = filtered.filter(video => video.hasRating);
    }

    setFilteredVideos(filtered);
    
    // Select the first video if available
    if (filtered.length > 0 && !selectedVideo) {
      setSelectedVideo(filtered[0]);
    } else if (filtered.length === 0) {
      setSelectedVideo(null);
    }
  };

  const handleVideoSelect = (video: VideoItem) => {
    console.log('🎦 Selected video with blurhash:', video.blurhash);
    setSelectedVideo(video);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE');
  };

  const formatDisplayDate = (date: Date | null) => {
    if (!date) return '';
    return format(date, "EEEE, d. MMMM yyyy", { locale: dateFnsDe });
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const handleOpenCalendar = () => {
    setCalendarOpen(true);
  };

  const handleHideFailuresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHideFailures(event.target.checked);
  };

  const handleRatedFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyRated(event.target.checked);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    
    // Navigation zu anderen Seiten basierend auf Tab-Auswahl
    switch (newValue) {
      case 0:
        navigate('/kameras');  // Kameras
        break;
      case 1:
        // Wir sind bereits auf der Meldungen/Archiv-Seite
        break;
      case 2:
        navigate('/einstellungen');  // Einstellungen
        break;
    }
  };

  // Update navigation functions to find days with videos
  const handlePreviousDay = async () => {
    if (!selectedDate || navigating || navigatingPrev) return;
    
    try {
      setNavigatingPrev(true);
      setNavigating(true);
      setFeedback(null);
      
      const prevDate = await findPrevDateWithVideos(selectedDate);
      
      if (prevDate) {
        console.log('⬅️ Found previous date with videos:', prevDate.toISOString());
        setSelectedDate(prevDate);
      } else {
        console.log('⬅️ No previous date with videos found');
        setNavigatingPrev(false);
        setNavigating(false);
        setFeedback('Keine früheren Videos gefunden');
        // Hide feedback after a few seconds
        setTimeout(() => setFeedback(null), 3000);
      }
    } catch (err) {
      console.error('❌ Error navigating to previous date:', err);
      setNavigatingPrev(false);
      setNavigating(false);
      setFeedback('Fehler bei der Navigation');
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  const handleNextDay = async () => {
    if (!selectedDate || navigating || navigatingNext) return;
    
    try {
      setNavigatingNext(true);
      setNavigating(true);
      setFeedback(null);
      
      const nextDate = await findNextDateWithVideos(selectedDate);
      
      if (nextDate) {
        console.log('➡️ Found next date with videos:', nextDate.toISOString());
        setSelectedDate(nextDate);
      } else {
        console.log('➡️ No next date with videos found');
        setNavigatingNext(false);
        setNavigating(false);
        setFeedback('Keine neueren Videos gefunden');
        // Hide feedback after a few seconds
        setTimeout(() => setFeedback(null), 3000);
      }
    } catch (err) {
      console.error('❌ Error navigating to next date:', err);
      setNavigatingNext(false);
      setNavigating(false);
      setFeedback('Fehler bei der Navigation');
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header mit Navigation */}
      <Box component="header" sx={{ mb: 0 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
            <Link 
              component="button" 
              onClick={() => navigate('/')}
              underline="none"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography variant="h5" component="h1" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'text.primary' }}>
                WashProTech
              </Typography>
            </Link>
          </Box>
          
          <Paper 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              marginBottom: 0
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="Navigation"
              variant="fullWidth"
              centered
              sx={{
                marginBottom: 0,
                paddingBottom: 0,
                '& .MuiTabs-scroller': {
                  marginBottom: '0px !important'
                },
                '& .MuiTab-root': {
                  py: 2,
                  minHeight: '60px',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    bgcolor: 'transparent',
                    transition: 'background-color 0.3s'
                  },
                  '&.Mui-selected': {
                    fontWeight: 'bold',
                    color: 'primary.main',
                    '&::after': {
                      bgcolor: 'primary.main'
                    }
                  }
                }
              }}
            >
              <Tab icon={<VideocamIcon />} label="Kameras" />
              <Tab icon={<NotificationsIcon />} label="Meldungen" />
              <Tab icon={<SettingsIcon />} label="Einstellung" />
            </Tabs>
          </Paper>
        </Container>
      </Box>

      {/* Hauptinhalt */}
      <Container maxWidth="lg" sx={{ flexGrow: 1, mb: 4, pt: 0 }}>
        <Box sx={{ display: 'flex', flex: 1, height: 'calc(100vh - 180px)' }}>
          <Grid container spacing={2}>
            {/* Left Column: Date picker and Video List */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  borderRadius: 1,
                  position: 'relative',
                  boxShadow: 'none',
                  border: '1px solid rgba(0, 0, 0, 0.12)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <IconButton 
                    onClick={handlePreviousDay} 
                    size="small"
                    disabled={navigating || loading}
                  >
                    {navigatingPrev ? 
                      <CircularProgress size={20} thickness={5} /> : 
                      <ArrowBackIosIcon fontSize="small" />
                    }
                  </IconButton>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      flex: 1,
                      justifyContent: 'center' 
                    }}
                    onClick={handleOpenCalendar}
                  >
                    <Typography variant="h6">
                      {selectedDate ? formatDisplayDate(selectedDate) : 'Datum wählen'}
                    </Typography>
                    <CalendarTodayIcon color="primary" fontSize="small" sx={{ ml: 1 }} />
                  </Box>
                  
                  <IconButton 
                    onClick={handleNextDay} 
                    size="small"
                    disabled={navigating || loading}
                  >
                    {navigatingNext ? 
                      <CircularProgress size={20} thickness={5} /> : 
                      <ArrowForwardIosIcon fontSize="small" />
                    }
                  </IconButton>
                </Box>
                
                {feedback && (
                  <Box 
                    sx={{
                      position: 'absolute',
                      bottom: '-15px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'info.main',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      zIndex: 10,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {feedback}
                  </Box>
                )}
              </Paper>

              {/* Calendar Dialog */}
              <Dialog 
                open={calendarOpen} 
                onClose={() => setCalendarOpen(false)}
                PaperProps={{
                  sx: { 
                    borderRadius: 2,
                    width: 'auto',
                    maxWidth: '100%'
                  }
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={selectedDate}
                    onChange={handleDateChange}
                    slotProps={{
                      actionBar: {
                        actions: ['today', 'cancel', 'accept']
                      }
                    }}
                  />
                </LocalizationProvider>
              </Dialog>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 1,
                  mb: 1,
                  backgroundColor: '#ffffff',
                  borderRadius: 1,
                  boxShadow: 'none',
                  border: '1px solid rgba(0, 0, 0, 0.12)'
                }}
              >
                <FormControlLabel
                  control={
                    <Switch 
                      checked={hideFailures}
                      onChange={handleHideFailuresChange}
                      color="success"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  }
                  label="Fehlschläge ausblenden"
                  sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={showOnlyRated}
                      onChange={handleRatedFilterChange}
                      size="small"
                    />
                  }
                  label="Bewertet"
                  sx={{ ml: 5, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
              </Box>

              <Paper sx={{ 
                p: 0, 
                flex: 1,
                overflow: 'auto', 
                display: 'flex', 
                flexDirection: 'column', 
                boxShadow: 'none', 
                border: '1px solid rgba(0, 0, 0, 0.12)',
                mb: 2
              }}>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
                
                {error && (
                  <Box sx={{ p: 2 }}>
                    <Typography color="error">{error}</Typography>
                  </Box>
                )}
                
                {!loading && filteredVideos.length === 0 && (
                  <Typography sx={{ m: 2 }}>Keine Videos für die ausgewählten Filter gefunden</Typography>
                )}

                <Box sx={{ px: 2, flex: 1, pt: 2 }}>
                  {filteredVideos.map((video) => (
                    <Card 
                      key={video.id} 
                      sx={{ 
                        mb: 2, 
                        cursor: 'pointer',
                        borderRadius: 0,
                        overflow: 'hidden',
                        boxShadow: 'none',
                        border: 'none',
                        bgcolor: 'transparent',
                        p: 0
                      }}
                      onClick={() => handleVideoSelect(video)}
                    >
                      <Box sx={{ display: 'flex', width: '100%' }}>
                        <Box
                          sx={{ 
                            width: 200, 
                            aspectRatio: '16/9',
                            position: 'relative',
                            borderRadius: '6px',
                            border: '2px solid',
                            borderColor: selectedVideo?.id === video.id ? '#2ecc71' : 'transparent',
                            overflow: 'hidden'
                          }}
                        >
                          <BlurhashImage
                            src={video.thumbnailUrl}
                            blurhash={video.blurhash}
                            alt={video.fileName}
                            width="100%"
                            height="100%"
                          />
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          px: 2,
                          py: 0,
                          width: '100%'
                        }}>
                          <Box>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 'medium', 
                                mb: 0.5,
                                color: selectedVideo?.id === video.id ? '#2ecc71' : 'inherit'
                              }}
                            >
                              {video.cameraName}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mb: 0.5,
                                color: selectedVideo?.id === video.id ? '#2ecc71' : 'text.secondary'
                              }}
                            >
                              {video.waschstrasse}
                            </Typography>
                            
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mb: 0.5,
                                color: selectedVideo?.id === video.id ? '#2ecc71' : 'text.secondary'
                              }}
                            >
                              {formatDate(video.timeStamp)}
                            </Typography>
                            
                            {video.richtigAusgeloest === true ? (
                              <Box 
                                sx={{ 
                                  bgcolor: 'rgba(46, 204, 113, 0.15)', 
                                  color: 'rgb(46, 204, 113)',
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 4,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Richtig erkannt
                              </Box>
                            ) : video.richtigAusgeloest === false ? (
                              <Box 
                                sx={{ 
                                  bgcolor: 'rgba(231, 76, 60, 0.15)', 
                                  color: 'rgb(231, 76, 60)',
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 4,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Falsch erkannt
                              </Box>
                            ) : (
                              <Box 
                                sx={{ 
                                  bgcolor: 'rgba(231, 76, 60, 0.15)', 
                                  color: 'rgb(231, 76, 60)',
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 4,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Unbekannt
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
                
                {filteredVideos.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="body2" color="text.secondary">
                      {filteredVideos.length} Videos gefunden
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right Column: Video Preview and Metadata */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {selectedVideo ? (
                  <>
                    <Box sx={{ position: 'relative', width: '100%', pt: '56.25%' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                      >
                        <VideoPlayer
                          videoUrl={selectedVideo.videoUrl}
                          thumbnailUrl={selectedVideo.thumbnailUrl}
                          blurhash={selectedVideo.blurhash || 'LEHV6nWB2yk8pyo0adR*.7kCMdnj'}
                          controls={true}
                      />
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5">
                          {selectedVideo.cameraName}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(selectedVideo.timeStamp)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, mb: 1 }}>
                        <Typography variant="body2">
                          {selectedVideo.waschstrasse}
                        </Typography>
                        <Chip 
                          label={selectedVideo.richtigAusgeloest === true ? "Richtig erkannt" : selectedVideo.richtigAusgeloest === false ? "Falsch erkannt" : "Unbekannt"} 
                          size="small" 
                          color={selectedVideo.richtigAusgeloest === true ? "success" : selectedVideo.richtigAusgeloest === false ? "error" : "default"}
                          sx={{ 
                            '& .MuiChip-label': { 
                              px: 2,
                              color: selectedVideo.richtigAusgeloest === true ? 'white' : selectedVideo.richtigAusgeloest === false ? 'white' : undefined 
                            } 
                          }}
                        />
                      </Box>
                      
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CloudDownloadIcon />}
                        href={selectedVideo.videoUrl}
                        download={selectedVideo.fileName}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography>Kein Video ausgewählt</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Archive; 